import { createSignal, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Check, ClipboardCopy } from "@fleet/icons";
import { buildDiagnosticsReport, type DiagnosticSection } from "@fieldcheck/shared";

import { Button } from "@/components/ui/button";
import { useI18n } from "@/providers/i18n-provider";

/**
 * Copies a pasteable diagnostic report to the clipboard.
 *
 * The component only GATHERS data and delegates formatting to
 * `buildDiagnosticsReport` (shared, pure, tested). If the report were
 * assembled here, each app would have its own version and they would diverge
 * as soon as someone added a field.
 *
 * Labels: `Copy` and `Copied!` are used because they are already translated in
 * the eleven languages. A new string like "Copy diagnostics" would appear in
 * all of them without the i18n gate complaining, because it only validates what
 * is already in the catalog.
 */
/** How long "Copied!" remains before returning to the normal label. */
const CONFIRMATION_MS = 2000;

export function CopyDiagnosticsButton(props: {
  appName: string;
  /** The SAME model that the screen renders. */
  sections: () => DiagnosticSection[];
}) {
  const { t } = useI18n();
  const [copied, setCopied] = createSignal(false);
  const [error, setError] = createSignal(false);

  const copy = async () => {
    const text = buildDiagnosticsReport({
      appName: props.appName,
      timestamp: new Date().toISOString(),
      sections: props.sections(),
    });
    try {
      await navigator.clipboard.writeText(text);
      setError(false);
      setCopied(true);
      setTimeout(() => setCopied(false), CONFIRMATION_MS);
    } catch {
      // The clipboard fails without permission or outside a secure context. Staying
      // silent would leave the user believing it copied: flag the failure and leave
      // the report selectable as plan B.
      setError(true);
    }
  };

  return (
    <div class="space-y-2">
      <Button
        variant="outline"
        class="w-full gap-2"
        data-testid="copy-diagnostics"
        onClick={() => void copy()}
      >
        <Show when={copied()} fallback={<ClipboardCopy size={18} />}>
          <Check size={18} class="text-success" />
        </Show>
        {copied() ? t(msg`Copied!`) : t(msg`Copy`)}
      </Button>

      {/* Plan B when the clipboard is unavailable: the report on screen
          for manual selection. Without this, the button would be a dead end
          right in the environments that need debugging the most. */}
      <Show when={error()}>
        <textarea
          data-testid="diagnostics-fallback"
          class="h-40 w-full rounded-md border border-border bg-muted/30 p-2 font-mono text-xs"
          readonly
          onFocus={(e) => e.currentTarget.select()}
        >
          {buildDiagnosticsReport({
            appName: props.appName,
            timestamp: new Date().toISOString(),
            sections: props.sections(),
          })}
        </textarea>
      </Show>
    </div>
  );
}
